import { useEffect, useRef, useState } from "react";

/**
 * Spoken turn-by-turn prompts via the browser Speech Synthesis API. Display/UX
 * only — never affects route, ETA, fare, dispatch or lifecycle.
 *
 * Fires on STEP CHANGE (the active maneuver from useTurnByTurn):
 *   • when a maneuver becomes active → "In <distance>, turn left onto <road>."
 *   • again within ~40 m of it       → "Turn left onto <road>."
 * Each maneuver speaks at most once per phase.
 *
 * iOS Safari hardening:
 *   • `primeVoice()` MUST be called inside a user gesture (the voice toggle tap)
 *     — it speaks a short utterance to unlock the audio session and warm the
 *     voice list. Without this, every later speak() is silently dropped.
 *   • Safari silently SUSPENDS speechSynthesis after a while / when other audio
 *     plays; we run a `resume()` keep-alive while voice is enabled.
 *   • We pick an explicit en-GB/en voice and set `lang`, because an utterance
 *     with no resolvable voice never speaks on some iOS builds.
 */
const _plain = (html) =>
  typeof html === "string" ? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "";

const ACTION = {
  "turn-left": "Turn left", "turn-right": "Turn right",
  "turn-slight-left": "Slight left", "turn-slight-right": "Slight right",
  "turn-sharp-left": "Sharp left", "turn-sharp-right": "Sharp right",
  "uturn-left": "Make a U-turn", "uturn-right": "Make a U-turn",
  "roundabout-left": "At the roundabout", "roundabout-right": "At the roundabout",
  "fork-left": "Keep left", "fork-right": "Keep right",
  merge: "Merge", straight: "Continue straight",
  "ramp-left": "Take the ramp on the left", "ramp-right": "Take the ramp on the right",
};

const roadFrom = (instruction) => {
  const t = _plain(instruction);
  let m = t.match(/ onto (.+?)(?: toward| for |$)/i);
  if (m) return m[1].trim();
  m = t.match(/ on (.+?)(?: toward| for |$)/i);
  if (m) return m[1].trim();
  return null;
};

const fmt = (m) => {
  if (m == null || !Number.isFinite(m)) return null;
  if (m < 1000) return `${Math.max(0, Math.round(m / 10) * 10)} metres`;
  return `${(m / 1609.34).toFixed(1)} miles`;
};

const buildPhrase = (step, phase, distanceM) => {
  const action = ACTION[step.maneuver] || _plain(step.instruction) || "Continue";
  const road = roadFrom(step.instruction);
  const onto = road ? ` onto ${road}` : "";
  const dist = fmt(distanceM);
  if (phase === "far" && dist) return `In ${dist}, ${action.toLowerCase()}${onto}.`;
  return `${action}${onto}.`;
};

const supported = () => typeof window !== "undefined" && "speechSynthesis" in window;

// Resolve a sensible English voice once it's available (empty on first call).
const pickVoice = () => {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    return (
      voices.find((v) => /en[-_]GB/i.test(v.lang)) ||
      voices.find((v) => /^en/i.test(v.lang)) ||
      voices[0] ||
      null
    );
  } catch {
    return null;
  }
};

const speak = (text) => {
  if (!supported() || !text) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    u.lang = "en-GB";
    const v = pickVoice();
    if (v) u.voice = v;
    // iOS can leave the queue "speaking" but stalled; nudge it before queueing.
    window.speechSynthesis.resume();
    window.speechSynthesis.cancel();
    // A micro-delay after cancel avoids iOS dropping the next utterance.
    setTimeout(() => {
      try {
        window.speechSynthesis.resume();
        window.speechSynthesis.speak(u);
      } catch { /* non-fatal */ }
    }, 60);
  } catch { /* non-fatal */ }
};

// Unlock audio + warm the voice list — call inside a user gesture.
export function primeVoice() {
  if (!supported()) return;
  try {
    window.speechSynthesis.getVoices();
    // Some iOS builds need a voiceschanged tick before voices populate.
    window.speechSynthesis.onvoiceschanged = () => { window.speechSynthesis.getVoices(); };
    const u = new SpeechSynthesisUtterance("Voice navigation on.");
    u.rate = 1.0;
    u.lang = "en-GB";
    const v = pickVoice();
    if (v) u.voice = v;
    window.speechSynthesis.resume();
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

export function useNavVoice(enabled, step, distanceM, arrived = false) {
  // Per-maneuver band tracking: each maneuver announces at distance thresholds
  // (prepare → approach → now) instead of only on step change, so guidance is
  // driven by distance-to-maneuver like a real sat-nav.
  const spokenRef = useRef({ key: null, prepare: false, approach: false, now: false });
  const arrivedRef = useRef(false);
  // Diagnostics: surface the last spoken phrase + count so the navigator's debug
  // overlay can prove voice EVENTS fire on-device (distinguishing a real bug from
  // the iPhone ring/silent switch muting an otherwise-working synth).
  const [lastSpoken, setLastSpoken] = useState(null);
  const countRef = useRef(0);
  const say = (text) => {
    if (!text) return;
    countRef.current += 1;
    setLastSpoken({ text, n: countRef.current });
    speak(text);
  };

  // Stop + reset when muted / navigator closed.
  useEffect(() => {
    if (!supported()) return;
    if (!enabled) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      spokenRef.current = { key: null, prepare: false, approach: false, now: false };
      arrivedRef.current = false;
    }
  }, [enabled]);

  // iOS keep-alive: Safari suspends the synth engine after periods of silence
  // or when the page is backgrounded; a periodic resume() keeps it warm so the
  // next maneuver actually speaks.
  useEffect(() => {
    if (!enabled || !supported()) return undefined;
    const id = setInterval(() => {
      try {
        if (!window.speechSynthesis.speaking) window.speechSynthesis.resume();
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(id);
  }, [enabled]);

  // Arrival announcement — fires once when the engine reports arrival.
  useEffect(() => {
    if (!enabled || !supported()) return;
    if (arrived && !arrivedRef.current) {
      arrivedRef.current = true;
      say("You have arrived at your destination.");
    }
    if (!arrived) arrivedRef.current = false;
  }, [enabled, arrived]);

  // Distance-threshold maneuver guidance.
  useEffect(() => {
    if (!enabled || !supported() || !step || arrived) return;
    const key = _plain(step.instruction) || step.maneuver || "step";
    if (spokenRef.current.key !== key) {
      spokenRef.current = { key, prepare: false, approach: false, now: false };
    }
    const m = Number.isFinite(distanceM) ? distanceM : null;
    if (m == null) {
      // No distance yet — announce the upcoming maneuver once so the driver
      // knows what's next even before a distance fix lands.
      if (!spokenRef.current.prepare) {
        spokenRef.current.prepare = true;
        say(buildPhrase(step, "near", null));
      }
      return;
    }
    const s = spokenRef.current;
    // "Now" — at the turn. Mark all farther bands done so we don't backfill.
    if (m <= 30 && !s.now) {
      s.now = true; s.approach = true; s.prepare = true;
      say(`${ACTION[step.maneuver] || _plain(step.instruction) || "Continue"}${roadFrom(step.instruction) ? ` onto ${roadFrom(step.instruction)}` : ""} now.`);
      return;
    }
    // "Approach" — within ~150 m.
    if (m <= 150 && !s.approach) {
      s.approach = true; s.prepare = true;
      say(buildPhrase(step, "far", m));
      return;
    }
    // "Prepare" — within ~450 m (first heads-up for this maneuver).
    if (m <= 450 && !s.prepare) {
      s.prepare = true;
      say(buildPhrase(step, "far", m));
    }
  }, [enabled, step, distanceM, arrived]);

  return { supported: supported(), lastSpoken };
}
